
import { VITE_BACKEND_URL } from "@/api/config";

const DEFAULT_TIMEOUT = 20000;
const MAX_RETRIES = 1;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type SafeFetchOptions = Omit<RequestInit, "body"> & {
  body?: any;
  retryOnNetworkError?: boolean;
};

function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number
): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  return fetch(url, {
    ...options,
    signal: controller.signal,
  }).finally(() => clearTimeout(id));
}

export async function safeFetch(
  endpoint: string,
  options: SafeFetchOptions = {},
  retries?: number
): Promise<Response> {
  const {
    retryOnNetworkError,
    ...requestOptions
  } = options;

  const method = (requestOptions.method || "GET").toUpperCase();

  // Solo GET y HEAD se pueden reintentar automáticamente.
  const canRetry =
    retryOnNetworkError === true ||
    (retryOnNetworkError !== false &&
      (method === "GET" || method === "HEAD"));

  const retryCount =
    retries ?? (canRetry ? MAX_RETRIES : 0);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("token")
      : null;

  const isFormData =
    typeof FormData !== "undefined" &&
    requestOptions.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(requestOptions.headers as Record<string, string>),
  };

  if (!isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const url = `${VITE_BACKEND_URL}${endpoint}`;

  const body =
    requestOptions.body === undefined
      ? undefined
      : isFormData
        ? requestOptions.body
        : typeof requestOptions.body === "string"
          ? requestOptions.body
          : JSON.stringify(requestOptions.body);

  try {
    const res = await fetchWithTimeout(
      url,
      {
        ...requestOptions,
        headers,
        body,
      },
      DEFAULT_TIMEOUT
    );

    if (res.status === 401) {
      localStorage.removeItem("token");
      window.location.replace("/login");
      throw new Error("Unauthorized");
    }

    return res;
  } catch (err: any) {
    const isRetryable =
      err?.name === "AbortError" ||
      err?.message?.includes("fetch") ||
      err?.message?.includes("Network");

    if (isRetryable && canRetry && retryCount > 0) {
      await sleep(300);

      return safeFetch(
        endpoint,
        options,
        retryCount - 1
      );
    }

    throw err;
  }
}

safeFetch.json = async function <T = any>(
  endpoint: string,
  options: SafeFetchOptions = {},
  retries?: number
): Promise<T> {
  const res = await safeFetch(endpoint, options, retries);

  const text = await res.text();

  let data: any;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `Invalid JSON response (${res.status}) from ${endpoint}:\n${text}`
    );
  }

  if (!res.ok) {
    throw new Error(
      data?.error ||
        data?.message ||
        `HTTP ${res.status} en ${endpoint}`
    );
  }

  return data as T;
};
